import { StatusBar, Style } from "@capacitor/status-bar";
import { Capacitor } from "@capacitor/core";

/**
 * SystemBars service following Capacitor best practices
 * - Automatic route-based StatusBar management
 * - CSS env() variables handle safe areas automatically
 * - Prevents statusbar overlap issues
 * - No manual calls required
 */

export interface SystemBarsOptions {
  statusBarStyle?: "light" | "dark";
  overlaysWebView?: boolean;
}

/**
 * Route-based StatusBar configuration
 */
interface RouteConfig {
  pattern: RegExp;
  config: SystemBarsOptions;
}

/**
 * Route configurations for automatic StatusBar management
 * Using overlay: true for transparent status bar (Capacitor best practice)
 */
const ROUTE_CONFIGS: RouteConfig[] = [
  // Home screens - Fully transparent
  {
    pattern: /\/(tabs\/home|organizer-tabs\/home)$/,
    config: {
      statusBarStyle: "dark",
      overlaysWebView: true, // Transparent overlay for home screens
    },
  },
  // All other screens - Fully transparent
  {
    pattern: /.*/,
    config: {
      statusBarStyle: "dark",
      overlaysWebView: true, // Transparent overlay for all screens
    },
  },
];

export class SystemBarsService {
  private currentConfig: SystemBarsOptions | null = null;
  private isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;

  /**
   * Initialize SystemBars service for native platforms.
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    const pendingInitialization = this.initializationPromise;
    if (pendingInitialization !== null) {
      await pendingInitialization;
      return;
    }

    this.initializationPromise = (async () => {
      try {
        // Only initialize status bar for native platforms
        if (this.isNativePlatform()) {
          await this.initializeNativeStatusBar();
        }

        this.isInitialized = true;
      } catch (error) {
        console.error("SystemBars init error:", error);
      } finally {
        this.initializationPromise = null;
      }
    })();

    await this.initializationPromise;
  }

  /**
   * Initialize native status bar with Capacitor best practices
   */
  private async initializeNativeStatusBar(): Promise<void> {
    try {
      // CRITICAL: Enable transparent overlay - Capacitor best practice for modern UI
      await StatusBar.setOverlaysWebView({ overlay: true });

      // Set style to Dark for transparent status bar with light text (better for transparent overlay)
      await StatusBar.setStyle({ style: Style.Dark });

      // Ensure status bar background is completely transparent
      await StatusBar.setBackgroundColor({ color: "#00000000" }); // Fully transparent

      await StatusBar.show();

      this.currentConfig = {
        overlaysWebView: true,
        statusBarStyle: "dark",
      };

      // Initialize navigation bar transparency for Android
      await this.initializeNavigationBar();
    } catch (error) {
      console.error("Status bar init error:", error);
    }
  }

  /**
   * Apply a route-specific status bar configuration.
   * Route changes are driven by React Router (useLocation) in router/index.tsx.
   */
  public async applyRouteConfig(pathname: string): Promise<void> {
    if (!this.isNativePlatform()) return;

    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const routeConfig = ROUTE_CONFIGS.find((config) =>
        config.pattern.test(pathname),
      );

      if (routeConfig) {
        await this.updateSystemBars(routeConfig.config);
      }
    } catch (error) {
      console.error("Route-based StatusBar update error:", error);
    }
  }

  /**
   * Update status bar configuration with overlay control
   */
  public async updateSystemBars(options: SystemBarsOptions): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      const nextConfig: SystemBarsOptions = {
        ...this.currentConfig,
        ...options,
      };

      if (
        this.currentConfig?.statusBarStyle === nextConfig.statusBarStyle &&
        this.currentConfig?.overlaysWebView === nextConfig.overlaysWebView
      ) {
        return;
      }

      // Handle overlay setting first
      if (options.overlaysWebView !== undefined) {
        await StatusBar.setOverlaysWebView({
          overlay: options.overlaysWebView,
        });
      }

      if (options.statusBarStyle) {
        const style =
          options.statusBarStyle === "light" ? Style.Light : Style.Dark;
        await StatusBar.setStyle({ style });
      }

      // Ensure transparent background for overlay mode
      if (nextConfig.overlaysWebView) {
        await StatusBar.setBackgroundColor({ color: "#00000000" }); // Fully transparent
      }

      this.currentConfig = nextConfig;
    } catch (error) {
      console.error("Status bar update error:", error);
    }
  }

  /**
   * Reset to default state with proper overlay handling
   */
  public async reset(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      // Reset overlay to true (modern UI best practice)
      await StatusBar.setOverlaysWebView({ overlay: true });
      await StatusBar.setStyle({ style: Style.Dark });

      // Ensure completely transparent background
      await StatusBar.setBackgroundColor({ color: "#00000000" }); // Fully transparent

      this.currentConfig = {
        overlaysWebView: true,
        statusBarStyle: "dark",
      };
    } catch (error) {
      console.error("Status bar reset error:", error);
    }
  }

  /**
   * Route updates are owned by React Router; no manual listener cleanup needed.
   */
  public cleanup(): void {
    // No-op by design in route-driven mode.
  }

  /**
   * Initialize navigation bar transparency for Android
   */
  private async initializeNavigationBar(): Promise<void> {
    try {
      if (Capacitor.getPlatform() === "android") {
        // Set navigation bar to transparent
        await this.setNavigationBarTransparent();
      }
    } catch (error) {
      console.error("Navigation bar init error:", error);
    }
  }

  /**
   * Set navigation bar to transparent on Android
   */
  private async setNavigationBarTransparent(): Promise<void> {
    try {
      if (Capacitor.getPlatform() === "android") {
        // Use SafeArea plugin to configure navigation bar transparency

        // FIXED: Don't override CSS variables that are already properly set in index.css
        // The CSS already handles safe area properly with env() functions
        // Only set if not already defined to prevent conflicts
        const root = document.documentElement;
        const currentBottomValue = getComputedStyle(root).getPropertyValue(
          "--safe-area-inset-bottom",
        );

        if (!currentBottomValue || currentBottomValue === "0px") {
          root.style.setProperty(
            "--safe-area-inset-bottom",
            "env(safe-area-inset-bottom, 0px)",
          );
        }
      }
    } catch (error) {
      console.error("Navigation bar transparency error:", error);
      // FIXED: Only set fallback if CSS variables are not already properly defined
      const root = document.documentElement;
      const currentBottomValue = getComputedStyle(root).getPropertyValue(
        "--safe-area-inset-bottom",
      );

      if (!currentBottomValue || currentBottomValue === "0px") {
        root.style.setProperty(
          "--safe-area-inset-bottom",
          "env(safe-area-inset-bottom, 0px)",
        );
      }
    }
  }

  public isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }
}

// Export singleton instance
export const systemBarsService = new SystemBarsService();

// Essential utility functions - for internal use only
export const updateSystemBars = (options: SystemBarsOptions) => {
  return systemBarsService.updateSystemBars(options);
};

export const resetSystemBars = () => {
  return systemBarsService.reset();
};
